import { Component } from '@angular/core';
import { LeaseDetailService } from "@modules/private/lease/data/services/view/lease-detail.service";
import { ApiLeaseService } from '../../data/services/api/api-lease.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-lease-start-payment',
  templateUrl: './lease-start-payment.component.html',
  styleUrls: ['./lease-start-payment.component.scss'],
  providers: [MessageService]
})
export class LeaseStartPaymentComponent {
  constructor(
    public leaseDetailService: LeaseDetailService,
    private apiLeaseService: ApiLeaseService,
    private messageService: MessageService
  ) { }

  get e() {
    return this.leaseDetailService.entityPayment
  }

  get data() {
    return this.leaseDetailService.data as any
  }

  isPaymentRegistered(): boolean {
    const isPaid = this.leaseDetailService.getTermDataSafely()?.downPayment?.isPaid
    const ip = this.data?.initialPayment
    const hasAllSavedFields = !!(ip?.voucher && ip?.date && ip?.paymentType)
    return !!isPaid || hasAllSavedFields
  }

  /**
   * Envío del botón «Guardar Comprobante». Pasa por el mismo método que el autoguardado para que
   * los dos caminos den el mismo feedback: el del servicio de detalle no podía, porque el toast de
   * esta pantalla escucha la instancia de MessageService que provee el componente.
   */
  onSubmit(): void {
    this.savePaymentData();
  }

  /**
   * Manejar cuando se sube un archivo de comprobante de pago
   */
  onFileSelected(fileUrl: string): void {
    // Actualizar el FormControl
    this.leaseDetailService.entityPayment.patchValue({
      file: fileUrl
    });

    // Verificar si están completos los 3 elementos antes de guardar
    if (this.areAllFieldsComplete()) {
      this.savePaymentData();
    } else {
      // Mostrar mensaje sobre qué campos faltan para guardar automáticamente
      this.showIncompleteFieldsMessage();
    }
  }

  /**
   * Manejar cuando se elimina un archivo de comprobante
   */
  onFileDeleted(): void {
    // Limpiar el FormControl
    this.leaseDetailService.entityPayment.patchValue({
      file: ''
    });

    // Notificar que se eliminó el archivo pero no guardar automáticamente
    this.messageService.add({
      severity: 'info',
      summary: 'Archivo eliminado',
      detail: 'El comprobante de pago ha sido eliminado.'
    });
  }

  /**
   * Manejar descarga de archivos
   */
  onDownloadRequested(fileUrl: string): void {
    this.apiLeaseService.downloadDocumentByUrl(fileUrl).subscribe({
      next: (blob) => {
        if (!blob || blob.size === 0) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'El archivo descargado está vacío. Intente de nuevo.'
          });
          return;
        }

        const extension = fileUrl.includes('.') ? fileUrl.substring(fileUrl.lastIndexOf('.')) : '';

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'comprobante_pago_inicial' + extension;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Descarga',
          detail: 'Archivo descargado correctamente.'
        });
      },
      error: (error) => {
        console.error('Error al descargar archivo:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo descargar el archivo.'
        });
      }
    });
  }

  /**
   * Guardar los datos del pago inicial
   */
  /**
   * Guardar los datos del pago inicial
   */
  private savePaymentData(): void {
    this.leaseDetailService.loading = true;
    
    const initialPaymentData = {
      initialPayment: {
        date: this.leaseDetailService.entityPayment.value.date,
        paymentType: this.leaseDetailService.entityPayment.value.paymentWay,
        voucher: this.leaseDetailService.entityPayment.value.file
      }
    };

    this.apiLeaseService.registerFirstPayment(this.leaseDetailService.id, initialPaymentData)
      .subscribe({
        next: (response) => {
          this.leaseDetailService.loading = false;
          if (!response.error) {
            this.leaseDetailService.getLeaseDetail();
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Pago inicial guardado automáticamente.'
            });
            return;
          }
          //* El servicio convierte el HTTP 400 en un next con error:true, así que el callback error de
          //* abajo no se dispara nunca. Sin este else el rechazo del backend no dejaba rastro en pantalla:
          //* el botón volvía a su sitio y el comercial no tenía forma de saber que no se había guardado.
          this.messageService.add({
            severity: 'error',
            summary: 'No se registró el pago inicial',
            detail: response.msg || 'No se pudo guardar el pago inicial.',
            life: 8000
          });
        },
        error: (error) => {
          this.leaseDetailService.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo guardar el pago inicial.'
          });
        }
      });
  }

  /**
   * Manejar cambios en la forma de pago
   */
  onPaymentWayChange(): void {
    // Solo validar el campo sin guardar automáticamente
    this.validateField('paymentWay');
  }

  /**
   * Manejar cambios en la fecha
   */
  onDateChange(): void {
    // Solo validar el campo sin guardar automáticamente
    this.validateField('date');
  }

  /**
   * Validar un campo específico sin guardar
   */
  private validateField(fieldName: string): void {
    const control = this.leaseDetailService.entityPayment.get(fieldName);
    if (control) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }
  }

  /**
   * Verificar si todos los campos requeridos están completos
   */
  private areAllFieldsComplete(): boolean {
    const formValue = this.leaseDetailService.entityPayment.value;
    
    const hasPaymentWay = formValue.paymentWay && formValue.paymentWay.trim() !== '';
    const hasDate = formValue.date && formValue.date.trim() !== '';
    const hasFile = formValue.file && formValue.file.trim() !== '';

    return hasPaymentWay && hasDate && hasFile;
  }

  /**
   * Mostrar mensaje sobre campos faltantes
   */
  private showIncompleteFieldsMessage(): void {
    const formValue = this.leaseDetailService.entityPayment.value;
    const missingItems = [];
    
    if (!formValue.paymentWay || formValue.paymentWay.trim() === '') {
      missingItems.push('forma de pago');
    }
    if (!formValue.date || formValue.date.trim() === '') {
      missingItems.push('fecha');
    }
    if (!formValue.file || formValue.file.trim() === '') {
      missingItems.push('comprobante de pago');
    }

    if (missingItems.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Información incompleta',
        detail: `Para guardar automáticamente, complete: ${missingItems.join(', ')}`
      });
    }
  }
}
